import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthorizedApiService } from '../../services/authorized-api.service';
import { AppStateService } from '../../services/app-state.service';
import { TranslateService } from '../../services/translate.service';
import { WaitlistOptions } from '../../models/Order';
import { ActionButtonComponent } from '../buttons/action-button.component';
import { LoadingSpinnerComponent } from '../ui/loading-spinner-component';
import { loadStoredContact, storeContact } from '../../utils/stored-contact';
import { WAITLIST_ERRORS } from './waitlist-errors';
import { TableNeedsChipsComponent } from '../table-needs/table-needs-chips.component';
import { TableNeed, tableNeeds, toggleNeed } from '../../utils/table-needs';

// tablebooking.max_party_size when unset, as in the booking
const DEFAULT_MAX_PARTY = 10;

// The walk-in queue, reached by a QR code at the entrance. Only open when the venue lets guests queue themselves.
@Component({
  selector: 'app-waitlist-queue',
  standalone: true,
  imports: [CommonModule, FormsModule, ActionButtonComponent, LoadingSpinnerComponent, TableNeedsChipsComponent],
  template: `
    <div class="max-w-lg mx-auto flex-col pt-20 text-theme-text">
      <div class="flex-1 bg-theme-surface-95 backdrop-blur-2xl rounded-t-3xl min-h-[calc(100vh-5rem)]">

        <div class="pt-6 px-8">
          <div class="font-bold text-xl">{{ getString('Ställ dig i kön') }}</div>
        </div>

        <div class="px-8 pb-12">
          @if (isLoading()) {
            <div class="flex justify-center py-12">
              <app-loading-spinner size="lg"></app-loading-spinner>
            </div>
          } @else if (!options()?.guest_queue) {
            <div class="text-center pt-12">
              <i class="fas fa-hand-wave text-4xl text-theme-text3 mb-4"></i>
              <p class="text-theme-text2">{{ getString('Det går inte att ställa sig i kön här.') }}</p>
            </div>
          } @else {
            <!-- Queue info -->
            <div class="mt-6 px-4 py-4 bg-theme-surface2 rounded-xl">
              <div class="font-bold">{{ options()?.venue_name }}</div>
              <div class="flex flex-wrap gap-x-4 gap-y-1 text-sm text-theme-text2 mt-2">
                <span class="inline-flex items-center gap-1.5">
                  <i class="fas fa-users text-xs"></i>
                  <span>{{ queueLengthText() }}</span>
                </span>
                @if (partySize() && options()?.estimated_wait_minutes != null) {
                  <span class="inline-flex items-center gap-1.5">
                    <i class="fas fa-clock text-xs"></i>
                    <span>{{ waitText() }}</span>
                  </span>
                }
              </div>
            </div>

            <div class="pt-6 space-y-4">
              <div>
                <label class="block font-medium mb-2">{{ getString('Antal personer') }}</label>
                <div class="grid grid-cols-4 gap-2">
                  @for (count of partyOptions(); track count) {
                    <button
                      type="button"
                      class="py-3 rounded-xl text-center font-medium transition-all active:scale-95"
                      [class.bg-theme-primary]="partySize() === count"
                      [class.text-theme-contrast]="partySize() === count"
                      [class.bg-theme-surface2]="partySize() !== count"
                      (click)="selectPartySize(count)">
                      {{ count }}
                    </button>
                  }
                </div>
                @if (contactEmail()) {
                  <!-- Larger parties than the queue takes write to the venue (its mail goes to the mail inbox) -->
                  <a class="block text-sm text-theme-text3 underline mt-2"
                     [href]="largePartyMailto()">{{ getString('Fler än %s i sällskapet? Mejla oss').replace('%s', '' + maxParty()) }}</a>
                }
              </div>

              <!-- What the party needs of its table: the wait counts only tables with it -->
              <app-table-needs-chips [offered]="offeredNeeds()" [picked]="needs()" (toggle)="toggleNeed($event)" />

              <div>
                <label class="block font-medium mb-1">{{ getString('Namn') }}</label>
                <input
                  type="text"
                  class="w-full px-4 py-3 bg-theme-surface2 rounded-xl
                         focus:outline-none focus:ring-2 focus:ring-theme-primary"
                  [(ngModel)]="name"
                  [placeholder]="getString('Ditt namn')">
              </div>

              <div>
                <label class="block font-medium mb-1">{{ getString('Mobilnummer') }}</label>
                <input
                  type="tel"
                  class="w-full px-4 py-3 bg-theme-surface2 rounded-xl
                         focus:outline-none focus:ring-2 focus:ring-theme-primary"
                  [(ngModel)]="phone"
                  placeholder="070-123 45 67">
                <p class="text-sm text-theme-text3 mt-1">{{ getString('Vi skickar ett SMS när ditt bord är klart.') }}</p>
              </div>
            </div>

            @if (errorMessage()) {
              <div class="mt-6 p-4 bg-red-100 border border-red-300 rounded-xl text-red-800 text-sm">
                <div class="flex items-center gap-2">
                  <i class="fas fa-exclamation-circle"></i>
                  <span>{{ errorMessage() }}</span>
                </div>
              </div>
            }

            <div class="mt-8">
              @if (isSubmitting()) {
                <div class="flex justify-center py-4">
                  <app-loading-spinner size="lg"></app-loading-spinner>
                </div>
              } @else {
                <app-action-button
                  [text]="getString('Ställ mig i kön')"
                  [disabled]="!isValid()"
                  (onClick)="join()">
                </app-action-button>
              }
            </div>
          }
        </div>
      </div>
    </div>
  `
})
export class WaitlistQueueComponent implements OnInit, OnDestroy {
  private api = inject(AuthorizedApiService);
  private appState = inject(AppStateService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private sub?: Subscription;
  private venueId = '';

  options = signal<WaitlistOptions | undefined>(undefined);
  isLoading = signal(true);
  partySize = signal<number | undefined>(undefined);
  // The needs the guest can tick (those the venue has tables for), and those ticked
  offeredNeeds = signal<TableNeed[]>([]);
  needs = signal<string[]>([]);
  partyOptions = signal<number[]>([]);
  maxParty = signal(DEFAULT_MAX_PARTY);
  contactEmail = signal<string | undefined>(undefined);
  isSubmitting = signal(false);
  errorMessage = signal<string | undefined>(undefined);
  name = '';
  phone = '';

  getString(s: string) {
    return this.translate.getString(s);
  }

  ngOnInit() {
    this.venueId = this.route.snapshot.paramMap.get('venue_id') ?? '';
    const stored = loadStoredContact();
    this.name = stored?.name ?? '';
    this.phone = stored?.phone ?? '';

    this.sub = this.appState.observeAppState().subscribe(appState => {
      // The same limit as the booking: the venue's tablebooking.max_party_size
      const max = appState?.cfg?.tablebooking?.max_party_size ?? DEFAULT_MAX_PARTY;
      this.maxParty.set(max);
      this.partyOptions.set(Array.from({ length: max }, (_, i) => i + 1));
      this.contactEmail.set(appState?.cfg?.email || undefined);
    });
    this.fetchOptions();
    this.api.getTableNeeds(this.venueId).subscribe({
      next: response => this.offeredNeeds.set(tableNeeds(response.needs)),
      error: () => this.offeredNeeds.set([]),
    });
  }

  largePartyMailto(): string {
    const subject = this.getString('Bord för fler än %s').replace('%s', '' + this.maxParty());
    return `mailto:${this.contactEmail()}?subject=${encodeURIComponent(subject)}`;
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  private fetchOptions() {
    this.api.waitlistOptions(this.venueId, this.partySize(), undefined, this.needs()).subscribe({
      next: (response) => {
        this.options.set(response.success ? response : undefined);
        this.isLoading.set(false);
      },
      error: () => {
        this.options.set(undefined);
        this.isLoading.set(false);
      }
    });
  }

  selectPartySize(count: number) {
    this.partySize.set(count);
    this.fetchOptions();
  }

  // Another need, another wait: only tables with the needs count
  toggleNeed(code: string) {
    this.needs.update(picked => toggleNeed(picked, code));
    this.fetchOptions();
  }

  queueLengthText(): string {
    const length = this.options()?.queue_length ?? 0;
    if (length === 0) return this.getString('Ingen kö just nu');
    if (length === 1) return this.getString('1 sällskap i kön');
    return this.getString('%s sällskap i kön').replace('%s', String(length));
  }

  waitText(): string {
    const minutes = this.options()?.estimated_wait_minutes ?? 0;
    if (minutes <= 0) return this.getString('Bord kan finnas nu');
    return this.getString('Väntetid ca %s min').replace('%s', String(minutes));
  }

  isValid(): boolean {
    const digits = this.phone.replace(/\D/g, '');
    return !!(this.partySize() && this.name.trim() && digits.length >= 7);
  }

  join() {
    this.isSubmitting.set(true);
    this.errorMessage.set(undefined);
    const stored = loadStoredContact();
    storeContact({ name: this.name, phone: this.phone, email: stored?.email ?? '', message: stored?.message });

    this.api.waitlistJoin(this.venueId, {
      mode: 'queue',
      party_size: this.partySize()!,
      guest_name: this.name.trim(),
      guest_phone: this.phone.trim(),
      needs: this.needs().length ? this.needs() : undefined,
    }).subscribe({
      next: (response) => {
        this.isSubmitting.set(false);
        if (response.success && response.ref && response.token) {
          this.router.navigate(['/', this.venueId, 'waitlist', response.ref], { queryParams: { t: response.token } });
        } else {
          const errorKey = response.error || 'unknown';
          this.errorMessage.set(this.getString(WAITLIST_ERRORS[errorKey] || 'Det gick inte att ställa dig i kön. Försök igen.'));
        }
      },
      error: () => {
        this.isSubmitting.set(false);
        this.errorMessage.set(this.getString('Det gick inte att ställa dig i kön. Försök igen.'));
      }
    });
  }
}
